import React, { useEffect, useState } from 'react';
import axios from '../axiosConfig';
import { saveAs } from 'file-saver';
import { Packer } from 'docx';
import { buildWorksheetDocument } from '../utils/worksheetDocument';
import { prepareQuestionVisuals } from '../utils/assessmentImages';
import { assessmentTranslator } from '../utils/assessmentLanguage';
import GenerationLanguageToggle from './GenerationLanguageToggle';
import CurriculumSources from './CurriculumSources';
import AssessmentPreview from './AssessmentPreview';
import { normalizeAiError } from '../services/ai.js';
import { CAPS_GRADES, CAPS_SUBJECTS } from '../utils/capsOptions.js';
import './AIWorksheetBuilder.css';

const EMPTY_FORM = { grade: '', subject: '', term: '1', topic: '', difficulty: 'Core', totalMarks: 25, questionCount: 5, additionalContext: '', outputLanguage: 'English' };
const WORKSHEET_DRAFT_KEY = 'marksure_worksheet_draft';

export default function AIWorksheetBuilder() {
    const [form, setForm] = useState(EMPTY_FORM);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    const updateForm = (field, value) => setForm((current) => ({ ...current, [field]: value }));

    useEffect(() => {
        try {
            const savedDraft = localStorage.getItem(WORKSHEET_DRAFT_KEY);
            if (!savedDraft) {
                return;
            }

            const parsedDraft = JSON.parse(savedDraft);
            if (parsedDraft && typeof parsedDraft === 'object') {
                setForm((current) => ({ ...current, ...parsedDraft, outputLanguage: parsedDraft.outputLanguage === 'Afrikaans' ? 'Afrikaans' : 'English' }));
            }
            localStorage.removeItem(WORKSHEET_DRAFT_KEY);
        } catch {
            localStorage.removeItem(WORKSHEET_DRAFT_KEY);
        }
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');
        setLoading(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/ai/worksheet.php`, form, { timeout: 300000 });
            setResult({ ...response.data, outputLanguage: form.outputLanguage, term: form.term });
        } catch (requestError) {
            setError(normalizeAiError(requestError));
        } finally {
            setLoading(false);
        }
    };

    const handleExportWord = async () => {
        if (!result) return;
        setExporting(true);
        setError('');
        try {
            const translate = assessmentTranslator(result.outputLanguage || form.outputLanguage);
            const visuals = await prepareQuestionVisuals(result.questions || [], { notToScale: translate('Not drawn to scale') });
            const blob = await Packer.toBlob(buildWorksheetDocument(result, form, visuals));
            const safeName = `${result.subject || form.subject}_Grade${result.grade || form.grade}_Worksheet`.replace(/[^a-z0-9]+/gi, '_');
            saveAs(blob, `${safeName}.docx`);
        } catch (exportError) {
            setError(`The Word document could not be created: ${exportError.message}`);
        } finally {
            setExporting(false);
        }
    };

    return <div className="worksheet-studio">
        <section className="worksheet-brief"><p className="worksheet-eyebrow">CAPS Worksheet Studio</p><h1>Create Worksheets with AI.</h1><p>Create a classroom-ready learner worksheet with a complete, step-by-step marking memorandum.</p></section>
        <section className="worksheet-builder-panel">
            <div className="worksheet-panel-heading"><div><p className="worksheet-eyebrow">Worksheet Brief</p><h2>Tell us what learners need to practise</h2></div><span className="worksheet-panel-heading__badge">AI assisted</span></div>
            <form onSubmit={handleSubmit} className="worksheet-form">
                <GenerationLanguageToggle value={form.outputLanguage} onChange={(value) => updateForm('outputLanguage', value)} disabled={loading} />
                <div className="worksheet-form__grid worksheet-form__grid--three"><label>Grade<select required value={form.grade} onChange={(event) => updateForm('grade', event.target.value)}><option value="">Select grade</option>{!CAPS_GRADES.some((grade) => grade.value === form.grade) && form.grade && <option value={form.grade}>{form.grade}</option>}{CAPS_GRADES.map((grade) => <option key={grade.value} value={grade.value}>{grade.label}</option>)}</select></label><label>Subject<select required value={form.subject} onChange={(event) => updateForm('subject', event.target.value)}><option value="">Select subject</option>{!CAPS_SUBJECTS.includes(form.subject) && form.subject && <option value={form.subject}>{form.subject}</option>}{CAPS_SUBJECTS.map((subject) => <option key={subject} value={subject}>{subject}</option>)}</select></label><label>Term<select value={form.term} onChange={(event) => updateForm('term', event.target.value)}>{[1, 2, 3, 4].map((term) => <option key={term} value={term}>Term {term}</option>)}</select></label></div>
                <label>Topic or concept<input required value={form.topic} onChange={(event) => updateForm('topic', event.target.value)} placeholder="e.g. Solving linear equations" /></label>
                <div className="worksheet-form__grid"><label>Difficulty<select value={form.difficulty} onChange={(event) => updateForm('difficulty', event.target.value)}><option value="Core">Core: standard CAPS</option><option value="Support">Support: scaffolded</option><option value="Extension">Extension: higher order thinking</option></select></label><label>Total marks<input type="number" min="5" max="100" value={form.totalMarks} onChange={(event) => updateForm('totalMarks', Number(event.target.value) || 25)} /></label><label>Main questions<input type="number" min="1" max="15" value={form.questionCount} onChange={(event) => updateForm('questionCount', Number(event.target.value) || 5)} /></label></div>
                <label>Teacher notes <span>(optional)</span><textarea rows={3} value={form.additionalContext} onChange={(event) => updateForm('additionalContext', event.target.value)} placeholder="e.g. include a bar graph and a labelled diagram, use South African examples, focus on word problems..." /></label>
                <button type="submit" className="worksheet-generate-button" disabled={loading}>{loading ? 'Building your worksheet...' : 'Generate worksheet and memo'}</button>
            </form>
            {loading && <div className="worksheet-loading" role="status"><span aria-hidden="true" /><p><strong>Building a balanced assessment.</strong> Allocating marks and preparing the memorandum.</p></div>}
            {error && <div className="worksheet-error">{error}</div>}
        </section>
        {result && <section className="worksheet-output">
            <div className="worksheet-output__toolbar"><div><p className="worksheet-eyebrow">Ready to download</p><h2>{result.title || `${form.subject} worksheet`}</h2></div><button type="button" className="worksheet-export-button" onClick={handleExportWord} disabled={exporting}>{exporting ? 'Preparing document...' : 'Download Word document'}</button></div>
            <div className="worksheet-meta"><span>Grade {result.grade || form.grade}</span><span>{result.subject || form.subject}</span><span>Term {result.term}</span><span>{result.outputLanguage}</span><strong>{result.totalMarks} marks</strong></div>
            <AssessmentPreview kind="worksheet" assessment={result} onChange={setResult} form={form} />
        </section>}
        {result && <CurriculumSources sources={result.curriculumSources} notice={result.curriculumNotice} />}
    </div>;
}
